import { RefObject, useEffect } from 'react';

const SETTLE_MS = 200;

/**
 * Makes a natively resizable textarea (resize: vertical) play nice with a
 * container-tracking height (height: 100% or flex stretch). Dragging the
 * native handle writes an inline height that permanently overrides the
 * tracking height, so the textarea stops following its container. Once a
 * drag settles, this converts the dragged height into an inline min-height
 * floor and clears the inline height so the tracking behaviour resumes.
 *
 * `active` must mirror the textarea's mount condition (e.g. `!loading`) so
 * the observer attaches once the element exists.
 */
const useTextareaResizeFloor = (elementRef: RefObject<HTMLTextAreaElement | null>, active = true) => {
	useEffect(() => {
		const el = elementRef.current;
		if (!active || !el) {
			return;
		}
		let settleTimer: number | null = null;
		let pointerHeld = false;

		// Only a native handle drag writes an inline height, so its presence is
		// the discriminator for "the user resized this". Converting while the
		// handle is still held would yank the textarea under the cursor, so
		// hold off until the pointer is released.
		const convert = () => {
			if (!el.style.height || pointerHeld) {
				return;
			}
			el.style.minHeight = el.style.height;
			el.style.height = '';
		};
		const observer = new ResizeObserver(() => {
			if (!el.style.height) {
				return;
			}
			if (settleTimer !== null) {
				clearTimeout(settleTimer);
			}
			settleTimer = window.setTimeout(convert, SETTLE_MS);
		});
		const onPointerDown = () => {
			pointerHeld = true;
		};
		// Window blur counts as a release — otherwise a drag released outside
		// the window leaves pointerHeld stuck and blocks the conversion.
		const onPointerUp = () => {
			pointerHeld = false;
			convert();
		};
		observer.observe(el);
		el.addEventListener('pointerdown', onPointerDown);
		window.addEventListener('pointerup', onPointerUp);
		window.addEventListener('pointercancel', onPointerUp);
		window.addEventListener('blur', onPointerUp);
		return () => {
			observer.disconnect();
			if (settleTimer !== null) {
				clearTimeout(settleTimer);
			}
			el.removeEventListener('pointerdown', onPointerDown);
			window.removeEventListener('pointerup', onPointerUp);
			window.removeEventListener('pointercancel', onPointerUp);
			window.removeEventListener('blur', onPointerUp);
		};
	}, [elementRef, active]);
};

export default useTextareaResizeFloor;
